import React, { useState } from "react";
import ProviderCommercialSections from "./ProviderCommercialSections";
import countries from "../../../countries.json";
import "./ProviderProductForm.css";
import { elakayAutoText } from "../../pageInside/dataElakay";


// Formulaire Provider Product partagé entre ProviderAddProduct.jsx (spec
// §11) et ProviderEditProduct.jsx (spec §12) -- une seule définition de
// champs pour ne jamais laisser les deux pages diverger (spec §23, même
// principe que le reste de l'audit : pas deux implémentations de la même
// chose). Sections 1/5/6/7/8/9/10/11/12/13/14 de la spec sont couvertes ici ;
// Images (2), Catégorie (3) et Filtres (4) sont volontairement de simples
// champs en attendant la réutilisation des vrais pickers Elakay (spec §23).
export const emptyFormState = {
  shopIds: undefined,
  name: "",
  description: "",
  brand: "",
  sku: "",
  providerSku: "",
  stock: "",
  basePrice: "",
  currency: "USD",
  minimumOrderQuantity: 1,
  // Trois méthodes commerciales : prix Simple Dropshipping, minimums par méthode, frais (chaque frais : unset|free|charged).
  dropshippingPrice: "",
  purchaseMinStore: "",
  purchaseMinShip: "",
  logisticsEnabled: false,
  purchasedFallsBack: false,
  fees: {
    dropshippingLogistics: { mode: "unset", amount: "", type: "per_unit" },
    purchasedStockLogistics: { mode: "unset", amount: "", type: "per_unit" },
    storage: { mode: "unset", amount: "", type: "per_unit" },
    shipping: { mode: "unset", amount: "", type: "flat" },
  },
  images: "",
  categoryId: "",
  countryOfOrigin: "",
  allowedCountries: "",
  quantityPricing: [],
  wholesaleEnabled: false,
  dropshippingEnabled: false,
  paidDropshippingEnabled: false,
  providerStorageEnabled: false,
  paEnabled: false,
  shippingByProviderEnabled: false,
  sellerPickupEnabled: false,
  localDeliveryEnabled: false,
  status: "draft",
  active: false,
  dropshippingPayment: {
    sellerPaymentAcceptanceAllowed: false,
    sellerPaymentAcceptanceFee: { enabled: false, type: "none", value: "" },
  },
};

// form state (strings from <input>) -> payload attendu par
// server_elakay/dropshippingInterne (spec §3/§4).
// Un frais du formulaire -> objet backend : "unset" = null (jamais 0), "free" = charge:false, "charged" = montant.
const feeOut = (fee) => {
  if (!fee || fee.mode === "unset") return null;
  if (fee.mode === "free") return { charge: false, amount: null, type: fee.type };
  return { charge: true, amount: fee.amount === "" ? null : Number(fee.amount), type: fee.type };
};
const feeIn = (fee, fallbackType) => {
  if (!fee) return { mode: "unset", amount: "", type: fallbackType };
  if (fee.charge === false) return { mode: "free", amount: "", type: fee.type || fallbackType };
  return { mode: "charged", amount: fee.amount ?? "", type: fee.type || fallbackType };
};
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export const buildPayload = (form) => {
  // spec §4 -- exactement un de unitPrice/discountPercent par palier, jamais
  // les deux (même règle que QuantityPricingTierSchema côté backend).
  const quantityPricing = form.quantityPricing
    .filter((tier) => tier.minQuantity)
    .map((tier) => ({
      minQuantity: Number(tier.minQuantity),
      maxQuantity: tier.maxQuantity ? Number(tier.maxQuantity) : null,
      unitPrice: tier.pricingType === "unitPrice" ? Number(tier.unitPrice) || 0 : null,
      discountPercent: tier.pricingType === "discountPercent" ? Number(tier.discountPercent) || 0 : null,
    }));

  return {
    boutiqueId: form.shopIds,
    name: form.name,
    description: form.description,
    brand: form.brand,
    sku: form.sku,
    providerSku: form.providerSku,
    stock: Number(form.stock) || 0,
    basePrice: Number(form.basePrice) || 0,
    currency: form.currency,
    minimumOrderQuantity: Number(form.minimumOrderQuantity) || 1,
    dropshippingPrice: numOrNull(form.dropshippingPrice),
    purchaseMinimums: { storeAtProvider: numOrNull(form.purchaseMinStore), shipToAddress: numOrNull(form.purchaseMinShip) },
    fulfillmentFees: {
      logisticsEnabled: Boolean(form.logisticsEnabled),
      purchasedStockFallsBackToDropshippingLogistics: Boolean(form.purchasedFallsBack),
      dropshippingLogistics: feeOut(form.fees?.dropshippingLogistics),
      purchasedStockLogistics: feeOut(form.fees?.purchasedStockLogistics),
      storage: feeOut(form.fees?.storage),
      shipping: feeOut(form.fees?.shipping),
    },
    images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
    categoryId: form.categoryId || null,
    // Where the product was actually MADE -- distinct from geo.allowedCountries below (where it can
    // be SOLD/shipped). Optional: never defaulted or guessed from the Provider's own address.
    countryOfOrigin: form.countryOfOrigin || null,
    geo: {
      allowedCountries: form.allowedCountries
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
      // Edit : ne jamais effacer silencieusement les listes que ce formulaire
      // n'édite pas (renvoyées telles quelles depuis le produit existant).
      blockedCountries: form.geoRest?.blockedCountries || [],
      allowedStates: form.geoRest?.allowedStates || [],
      blockedStates: form.geoRest?.blockedStates || [],
    },
    quantityPricing,
    wholesaleEnabled: form.wholesaleEnabled,
    dropshippingEnabled: form.dropshippingEnabled,
    paidDropshippingEnabled: form.paidDropshippingEnabled,
    providerStorageEnabled: form.providerStorageEnabled,
    paEnabled: form.paEnabled,
    shippingByProviderEnabled: form.shippingByProviderEnabled,
    sellerPickupEnabled: form.sellerPickupEnabled,
    localDeliveryEnabled: form.localDeliveryEnabled,
    status: form.status,
    active: form.active,
    dropshippingPayment: {
      sellerPaymentAcceptanceAllowed: Boolean(form.dropshippingPayment?.sellerPaymentAcceptanceAllowed),
      sellerPaymentAcceptanceFee: {
        enabled: Boolean(form.dropshippingPayment?.sellerPaymentAcceptanceFee?.enabled),
        type: form.dropshippingPayment?.sellerPaymentAcceptanceFee?.type || "none",
        value: form.dropshippingPayment?.sellerPaymentAcceptanceFee?.value === "" ? 0 : Number(form.dropshippingPayment?.sellerPaymentAcceptanceFee?.value) || 0,
      },
    },
  };
};

// Sens inverse de buildPayload -- document ProviderProduct tel que renvoyé
// par l'API (spec Phase 3/6) -> état de formulaire éditable (spec §12 : Edit
// ne recrée jamais le produit, tout se pré-remplit depuis l'existant).
export const formStateFromProduct = (product) => ({
  shopIds: product.boutiqueId?.length ? product.boutiqueId.map((s) => String(s?._id || s)) : undefined,
  name: product.name || "",
  description: product.description || "",
  brand: product.brand || "",
  sku: product.sku || "",
  providerSku: product.providerSku || "",
  stock: product.stock ?? 0,
  basePrice: product.basePrice ?? "",
  currency: product.currency || "USD",
  minimumOrderQuantity: product.minimumOrderQuantity ?? 1,
  dropshippingPrice: product.dropshippingPrice ?? "",
  purchaseMinStore: product.purchaseMinimums?.storeAtProvider ?? "",
  purchaseMinShip: product.purchaseMinimums?.shipToAddress ?? "",
  logisticsEnabled: Boolean(product.fulfillmentFees?.logisticsEnabled),
  purchasedFallsBack: Boolean(product.fulfillmentFees?.purchasedStockFallsBackToDropshippingLogistics),
  fees: {
    dropshippingLogistics: feeIn(product.fulfillmentFees?.dropshippingLogistics, "per_unit"),
    purchasedStockLogistics: feeIn(product.fulfillmentFees?.purchasedStockLogistics, "per_unit"),
    storage: feeIn(product.fulfillmentFees?.storage, "per_unit"),
    shipping: feeIn(product.fulfillmentFees?.shipping, "flat"),
  },
  images: (product.images || []).join(", "),
  categoryId: product.categoryId || "",
  countryOfOrigin: product.countryOfOrigin || "",
  allowedCountries: (product.geo?.allowedCountries || []).join(", "),
  geoRest: {
    blockedCountries: product.geo?.blockedCountries || [],
    allowedStates: product.geo?.allowedStates || [],
    blockedStates: product.geo?.blockedStates || [],
  },
  quantityPricing: (product.quantityPricing || []).map((tier) => ({
    minQuantity: tier.minQuantity ?? "",
    maxQuantity: tier.maxQuantity ?? "",
    pricingType: tier.discountPercent !== null && tier.discountPercent !== undefined ? "discountPercent" : "unitPrice",
    unitPrice: tier.unitPrice ?? "",
    discountPercent: tier.discountPercent ?? "",
  })),
  wholesaleEnabled: Boolean(product.wholesaleEnabled),
  dropshippingEnabled: Boolean(product.dropshippingEnabled),
  paidDropshippingEnabled: Boolean(product.paidDropshippingEnabled),
  providerStorageEnabled: Boolean(product.providerStorageEnabled),
  paEnabled: Boolean(product.paEnabled),
  shippingByProviderEnabled: Boolean(product.shippingByProviderEnabled),
  sellerPickupEnabled: Boolean(product.sellerPickupEnabled),
  localDeliveryEnabled: Boolean(product.localDeliveryEnabled),
  status: product.status || "draft",
  active: Boolean(product.active),
  dropshippingPayment: {
    sellerPaymentAcceptanceAllowed: Boolean(product.dropshippingPayment?.sellerPaymentAcceptanceAllowed),
    sellerPaymentAcceptanceFee: {
      enabled: Boolean(product.dropshippingPayment?.sellerPaymentAcceptanceFee?.enabled),
      type: product.dropshippingPayment?.sellerPaymentAcceptanceFee?.type || "none",
      value: product.dropshippingPayment?.sellerPaymentAcceptanceFee?.value ?? "",
    },
  },
});

// `onSubmit(payload, { publish })` does the actual API call + navigation; the
// form only builds the payload and surfaces whatever error it gets back
// (`{ error }`). Returning nothing/`{}` is treated as success.
const ProviderProductForm = ({
  title,
  hint,
  initialValues = emptyFormState,
  onSubmit,
  primaryLabel = "Save as draft",
  secondaryLabel = "Publish",
  footer = null,
}) => {
  const [form, setForm] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setField = (key) => (e) => {
    const value = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e, { publish } = {}) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError(elakayAutoText("h_18eef5848e8cd9", "Product name is required."));
    if (!form.basePrice) return setError(elakayAutoText("h_2e57806f6e2fc2", "Base price is required."));

    setSubmitting(true);
    const payload = buildPayload(form);
    if (publish) {
      payload.status = "active";
      payload.active = true;
    }
    const result = await onSubmit(payload, { publish: Boolean(publish) });
    setSubmitting(false);
    if (result?.error) {
      setError(result.error);
    }
  };

  return (
    <div className="provider-product-form">
      <h1>{title}</h1>
      {hint && <p className="provider-product-form__hint">{hint}</p>}

      <form onSubmit={handleSubmit}>
        <section>
          <h2>{elakayAutoText("h_521dcb66f68310", "1. General information")}</h2>
          <label>{elakayAutoText("h_709a23220f2c3d", "Name")}<input type="text" value={form.name} onChange={setField("name")} required />
          </label>
          <label>{elakayAutoText("h_55f8ebc805e65b", "Description")}<textarea rows={4} value={form.description} onChange={setField("description")} />
          </label>
          <label>{elakayAutoText("h_62b4aa574f708a", "Brand")}<input type="text" value={form.brand} onChange={setField("brand")} />
          </label>
          <label>{elakayAutoText("h_58ee936034c462", "Made In / Country of Origin")}<select value={form.countryOfOrigin} onChange={setField("countryOfOrigin")}>
              <option value="">{elakayAutoText("h_60f1e913db6145", "Not specified")}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <small className="provider-product-form__hint-inline">{elakayAutoText("h_b0d9684edf09e0", "Where this product is actually manufactured -- not your shop location or shipping origin.")}</small>
          </label>
        </section>

        <section>
          <h2>{elakayAutoText("h_d0148f66509f00", "2. Images")}</h2>
          <label>{elakayAutoText("h_8b7c0e76c93ef4", "Image URLs (comma-separated)")}<input type="text" value={form.images} onChange={setField("images")} placeholder="https://... , https://..." />
          </label>
        </section>

        <section>
          <h2>{elakayAutoText("h_951716fd43f473", "3. Category")}</h2>
          <label>{elakayAutoText("h_fdd7bd712b3f86", "Category ID")}<input type="text" value={form.categoryId} onChange={setField("categoryId")} placeholder={elakayAutoText("h_f57cb63eeeba30", "Elakay category id")} />
          </label>
        </section>

        <section>
          <h2>{elakayAutoText("h_61c4255c4857d6", "5. SKU")}</h2>
          <label>
            SKU
            <input type="text" value={form.sku} onChange={setField("sku")} />
          </label>
          <label>{elakayAutoText("h_d621f34b618bbe", "Provider SKU")}<input type="text" value={form.providerSku} onChange={setField("providerSku")} />
          </label>
        </section>

        <section>
          <h2>{elakayAutoText("h_a753d51585a298", "6. Stock")}</h2>
          <label>{elakayAutoText("h_ecac156c122fb1", "Stock quantity")}<input type="number" min="0" value={form.stock} onChange={setField("stock")} />
          </label>
        </section>

        <section>
          <h2>{elakayAutoText("h_22ef50ba5a8d70", "7. Base price")}</h2>
          <label>{elakayAutoText("h_3bd5ce68d286c1", "Base price (")}{form.currency})
            <input type="number" min="0" step="0.01" value={form.basePrice} onChange={setField("basePrice")} required />
          </label>
        </section>

        <ProviderCommercialSections form={form} setField={setField} setForm={setForm} />

        {error && <p className="provider-product-form__error">{error}</p>}

        <div className="provider-product-form__actions">
          <button type="submit" disabled={submitting}>
            {primaryLabel}
          </button>
          <button type="button" disabled={submitting} onClick={(e) => handleSubmit(e, { publish: true })}>
            {secondaryLabel}
          </button>
        </div>

        {footer}
      </form>
    </div>
  );
};

export default ProviderProductForm;
